import type { Location } from 'react-router-dom';

/**
 * Where to go after signing in, carried as `?next=` so it survives a hop
 * between the sign-in and sign-up screens and the email-confirm round trip
 * (Supabase sends the user back to `/login?next=...`).
 *
 * Only an in-app path is honoured: one leading slash, no scheme, no `//host`
 * or `/\host`, so a crafted link cannot bounce a fresh session off-site.
 */
export function safeNextPath(raw: string | null | undefined): string | null {
  if (!raw || !raw.startsWith('/')) return null;
  if (raw.startsWith('//') || raw.startsWith('/\\')) return null;
  return raw;
}

/** The return path of a sign-in screen: `?next=`, then the guard's `state.from`, then home. */
export function readNextPath(location: Location): string {
  const fromQuery = safeNextPath(new URLSearchParams(location.search).get('next'));
  if (fromQuery) return fromQuery;
  const fromState = safeNextPath((location.state as { from?: string } | null)?.from);
  return fromState ?? '/';
}

/** `path` with `?next=` appended, or `path` alone when there is nowhere special to go. */
export function withNext(path: string, next: string | null | undefined): string {
  const safe = safeNextPath(next);
  if (!safe || safe === '/') return path;
  return `${path}?next=${encodeURIComponent(safe)}`;
}
